import React, { FC } from 'react';
import { LockIcon } from '@clearcover/atlas';
import { GetLeaderboardResponse } from 'Rewards/services/endpoints/GetLeaderboard/types';
import { GetTierStatusResponse } from 'Rewards/services/endpoints/GetTierStatus/types';
import {
  GetRewardsConfigResponse,
  topProducerPrizeForPhase,
  policiesToNextTier,
  tierActFloors,
} from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { GetAgencyActRankResponse } from 'Rewards/services/endpoints/GetAgencyActRank/types';
import styles from './styles.module.css';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${String(d.getUTCFullYear()).slice(-2)}`;
}

function formatDateRange(start: string, end: string): string {
  return `Results for ${formatDate(start)} – ${formatDate(end)}`;
}

export type TieredRewardsCardProps = {
  leaderboard: GetLeaderboardResponse | null;
  tierStatus: GetTierStatusResponse | null;
  actRank: GetAgencyActRankResponse | null;
  config: GetRewardsConfigResponse | null;
  phaseCode: string | null;
  currentUserAgentId: string | null;
  loading: boolean;
};

export const TieredRewardsCard: FC<TieredRewardsCardProps> = ({
  leaderboard,
  tierStatus,
  actRank,
  config,
  phaseCode,
  currentUserAgentId,
  loading,
}) => {
  if (loading) {
    return (
      <div className={styles.card} data-testid="tiered-rewards-card">
        <p className={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Derive prize amount for this act/phase
  const prizeAmount =
    config && phaseCode ? topProducerPrizeForPhase(config, phaseCode) : null;

  // Derive date range: prefer actRank dates, fall back to phase dates
  let dateRangeText: string | null = null;
  if (actRank?.actStart && actRank?.actClose) {
    dateRangeText = formatDateRange(actRank.actStart, actRank.actClose);
  } else if (config?.phase?.start && config?.phase?.end) {
    dateRangeText = formatDateRange(config.phase.start, config.phase.end);
  }

  const producers = leaderboard?.producers ?? [];
  const isGold = tierStatus?.tier === 'GOLD';

  // Gap to next tier (AC8) — uses act_floor from program config.
  // Note: tier qualification in prime also requires a daily-ITD-rate threshold;
  // we intentionally show only the act_floor gap (matches AC8's "X more policies" framing).
  let gapMessage: string | null = null;
  if (!isGold && tierStatus && config) {
    const gap = policiesToNextTier(
      tierStatus.tier,
      tierStatus.actVolumeCount,
      config
    );
    if (gap) {
      const { nextTier, remaining } = gap;
      const nextTierLabel =
        nextTier === 'BRONZE'
          ? 'Bronze'
          : nextTier === 'SILVER'
            ? 'Silver'
            : 'Gold';
      gapMessage = `${remaining} more ${remaining === 1 ? 'policy' : 'policies'} to reach ${nextTierLabel}`;
    }
  }

  // Act close date for qualifier bar
  const actCloseText = actRank?.actClose ? formatDate(actRank.actClose) : null;

  // Policies remaining to qualify (AC10) — uses Gold act_floor from program config.
  // The qualifier bar always shows the gap to GOLD (the top tier needed to fully qualify).
  let qualifierCopy: React.ReactNode;
  if (isGold) {
    qualifierCopy = (
      <>
        Your agency has qualified — keep selling to lead the leaderboard.{' '}
        {/* TODO(DLC-30 AC11): confirm copy with design */}
      </>
    );
  } else {
    const floors = config ? tierActFloors(config) : null;
    const goldFloor = floors?.GOLD ?? null;
    const policiesSoFar = tierStatus?.actVolumeCount ?? 0;
    const remaining =
      goldFloor !== null ? Math.max(0, goldFloor - policiesSoFar) : null;
    const closePart = actCloseText ? ` by ${actCloseText}` : '';
    qualifierCopy =
      remaining !== null
        ? `Your agency must sell ${remaining} more ${remaining === 1 ? 'policy' : 'policies'}${closePart} to qualify`
        : 'Keep selling to qualify for this reward';
  }

  if (producers.length === 0) {
    return (
      <div className={styles.card} data-testid="tiered-rewards-card">
        <div className={styles.cardHeader}>
          <p className={styles.sectionLabel}>TIERED REWARDS</p>
          {dateRangeText && (
            <span className={styles.dateRange}>{dateRangeText}</span>
          )}
        </div>
        {prizeAmount != null && (
          <p className={styles.prizeHeadline}>Win ${prizeAmount}</p>
        )}
        <p className={styles.emptyText}>No leaderboard data available.</p>
      </div>
    );
  }

  return (
    <div className={styles.card} data-testid="tiered-rewards-card">
      {/* Header */}
      <div className={styles.cardHeader}>
        <p className={styles.sectionLabel}>TIERED REWARDS</p>
        {dateRangeText && (
          <span className={styles.dateRange}>{dateRangeText}</span>
        )}
      </div>

      {/* Prize headline — omitted when amount is null (AC2, graceful fallback) */}
      {prizeAmount != null && (
        <p
          className={styles.prizeHeadline}
          data-testid="tiered-rewards-prize-headline"
        >
          Win ${prizeAmount}
        </p>
      )}

      {/* Leaderboard rows */}
      <div
        className={styles.leaderboardList}
        data-testid="tiered-rewards-leaderboard"
      >
        {producers.map((producer, idx) => {
          // Competition ranking (ALL_TIED_WIN): rank = 1 + count of producers with strictly
          // higher policy_count. Producers are pre-sorted desc by the API.
          const rank =
            1 +
            producers.filter((p) => p.policy_count > producer.policy_count)
              .length;
          const isCurrentUser =
            currentUserAgentId !== null &&
            producer.agent_id === currentUserAgentId;
          // Every producer tied at rank 1 (i.e. sharing the highest policy_count) gets the
          // prize chip + lock affordance (AC4/AC5/AC6 — tiebreaker = ALL_TIED_WIN).
          const isFirst = rank === 1;

          return (
            <React.Fragment key={producer.agent_id}>
              <div
                className={`${styles.leaderboardRow}${isCurrentUser ? ` ${styles.leaderboardRowCurrent}` : ''}`}
                data-testid={`leaderboard-row-${idx + 1}`}
              >
                <span className={styles.rank}>#{rank}</span>
                <div className={styles.producerInfo}>
                  <div className={styles.producerName}>
                    {producer.agent_name ?? 'Agent'}
                  </div>
                  {isCurrentUser && (
                    <div
                      className={styles.youLabel}
                      data-testid="leaderboard-you-label"
                    >
                      You
                    </div>
                  )}
                </div>
                <div className={styles.policyCountCol}>
                  {isFirst && prizeAmount != null && (
                    <span
                      className={styles.prizeChip}
                      data-testid="leaderboard-rankone-prize"
                    >
                      ${prizeAmount}
                    </span>
                  )}
                  {isFirst && !isGold && (
                    <LockIcon
                      className={styles.lockIcon}
                      aria-label="Locked"
                      data-testid="leaderboard-rankone-lock"
                    />
                  )}
                  <span className={styles.policyCount}>
                    {producer.policy_count}
                  </span>
                </div>
              </div>

              {/* Gap message immediately after current user's row (AC8) */}
              {isCurrentUser && gapMessage && !isGold && (
                <p
                  className={styles.gapMessage}
                  data-testid="tiered-rewards-gap-message"
                >
                  {gapMessage}
                </p>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Bottom qualifier bar (AC10/AC11) */}
      <div
        className={`${styles.qualifierBar}${isGold ? ` ${styles.qualifierBarGold}` : ''}`}
        data-testid="tiered-rewards-qualifier-bar"
      >
        {!isGold && (
          <LockIcon
            className={styles.qualifierLock}
            aria-label="Not yet qualified"
            data-testid="tiered-rewards-qualifier-lock"
          />
        )}
        <span>{qualifierCopy}</span>
      </div>
    </div>
  );
};
